import { useEffect, useRef, useState, type ReactNode } from "react";

const SECTION_H = "100svh";
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

export function BleedSection({
  id, eyebrow, imgSrc, imgAlt, imgSide,
  children, bg = "#F8F8F8",
}: {
  id?: string; eyebrow: string; imgSrc: string; imgAlt: string;
  imgSide: "left" | "right"; children: ReactNode; bg?: string;
}) {
  const isLeft = imgSide === "left";
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id={id}
      className="relative w-full overflow-hidden hidden md:block"
      style={{ height: SECTION_H, backgroundColor: bg }}
    >
      <div
        className="absolute top-0 bottom-0 flex-none"
        style={{
          [isLeft ? "left" : "right"]: 0,
          width: "50vw",
          transform: inView ? "translate3d(0,0,0)" : `translate3d(${isLeft ? "-100%" : "100%"}, 0, 0)`,
          transition: `transform 1000ms ${EASE}`,
          willChange: "transform",
        }}
      >
        <img src={imgSrc} alt={imgAlt} className="w-full h-full block"
             style={{ objectFit: "cover", objectPosition: "center" }} />
      </div>
      <div className="relative z-10 flex items-center w-full h-full"
           style={{ justifyContent: isLeft ? "flex-end" : "flex-start" }}>
        <div className="flex flex-col justify-between h-full py-16"
             style={{
               width: "50vw",
               [isLeft ? "paddingLeft" : "paddingRight"]: "clamp(32px, 4vw, 80px)",
               [isLeft ? "paddingRight" : "paddingLeft"]: "clamp(32px, 5vw, 96px)",
               transform: inView ? "translate3d(0,0,0)" : "translate3d(0,20px,0)",
               opacity: inView ? 1 : 0,
               transition: `transform 800ms ${EASE} 200ms, opacity 800ms ${EASE} 200ms`,
               willChange: "transform, opacity",
             }}>
          <span className="eyebrow">{eyebrow}</span>
          <div>{children}</div>
        </div>
      </div>
    </section>
  );
}

export function BleedSectionMobile({
  eyebrow, imgSrc, imgAlt, children, bg = "#F8F8F8",
}: {
  eyebrow: string; imgSrc: string; imgAlt: string;
  children: ReactNode; bg?: string;
}) {
  return (
    <section className="w-full flex flex-col md:hidden py-16 px-5" style={{ backgroundColor: bg }}>
      <span className="eyebrow mb-8">{eyebrow}</span>
      <div className="w-full overflow-hidden mb-8" style={{ aspectRatio: "4/3" }}>
        <img src={imgSrc} alt={imgAlt} className="w-full h-full block" style={{ objectFit: "cover" }} />
      </div>
      {children}
    </section>
  );
}

export default BleedSection;